/*
 * Saved designs, kept in the customer's account.
 *
 * The saving itself is the configurator app's (the dtg-configurator app proxy): its runtime puts a Save
 * button beside Add to basket and posts the design, with a thumbnail, to /apps/dtg-configurator/api/
 * my-designs. A design needs a signed-in customer, so this file does what's around that:
 *
 *   - signed out, Save opens a short sheet ("Sign in to save") instead of jumping straight to the sign-in
 *     page, and the design saves itself once the visitor is back from signing in;
 *   - the app's green "Saved to your profile" line becomes the theme's toast, with a way to the list;
 *   - the button shows when this exact design is already saved, and doesn't save it twice;
 *   - the header's counts ([data-tcs-saved-count]) show how many designs the account holds;
 *   - the saved designs page (sections/saved-designs.liquid) lists them: open, copy the link, remove.
 *
 * Designs saved on a device before accounts (localStorage, tcs.savedDesigns.v1) are still shown on that
 * page, under the account's, so nothing anyone saved disappears.
 *
 *   window.tcsSaved.list({ fresh }) → Promise of designs, newest first ([] when signed out)
 *   window.tcsSaved.remove(id) → Promise
 *   'tcs:saved-change' fires on window whenever the list changes.
 */
(() => {
  if (window.tcsSaved) return;

  const account = window.tcsAccount || {};
  const signedIn = Boolean(account.signedIn);
  const savedPage = account.savedPage || '/pages/my-designs';
  const API = '/apps/dtg-configurator/api/my-designs';
  const CACHE = 'tcs.saved.v2';
  const PENDING = 'tcs.saveAfterSignIn';
  const LOCAL = 'tcs.savedDesigns.v1';
  const LAST = 'tcs.lastDesign.v1';

  const escapeHtml = (text) =>
    String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const svg = (paths, size = 18) =>
    `<svg class="tcs-icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
  const ICON = {
    bookmark: '<path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/>',
    link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
    trash: '<path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
    share: '<path d="M12 2v13"/><path d="m16 6-4-4-4 4"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>',
    arrow: '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>',
    x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
    check: '<path d="M20 6 9 17l-5-5"/>',
  };
  const toast = (options) => window.tcs?.toast(options);

  /* ---- A design's identity ----
   * The configurator keeps a whole design in the address (its c.* parameters). Two addresses are the
   * same design when their product path and c.* parameters match, whatever else is in them.
   */
  const designKey = (href) => {
    try {
      const url = new URL(href, window.location.origin);
      const params = [...url.searchParams].filter(([name]) => name.startsWith('c.')).sort(([a], [b]) => a.localeCompare(b));
      return url.pathname + (params.length ? '?' + new URLSearchParams(params).toString() : '');
    } catch (error) {
      return '';
    }
  };
  // A link to share or open: the design without the theme preview or tracking.
  const cleanLink = (href, absolute = false) => {
    try {
      const url = new URL(href, window.location.origin);
      ['preview_theme_id', '_pos', '_sid', '_ss', '_psq', '_v'].forEach((name) => url.searchParams.delete(name));
      return absolute ? url.href : url.pathname + url.search;
    } catch (error) {
      return href;
    }
  };

  /* ---- The account's list ----
   * GET my-designs answers { designs: [{ id, productTitle, productPath, productUrl, productImageUrl,
   * hasThumbnail, createdAt }] } (checked 19 Oct 2026). productUrl opens the design through a saved
   * configuration (?c.saved=<id>), whose values (size, finish, everything) are at
   * /apps/dtg-configurator/api/saved-configurations/<id>: details() reads them for the size and finish
   * on a card, and to recognise the design on screen as one already saved.
   */
  const SAVED_CONFIG = '/apps/dtg-configurator/api/saved-configurations';
  const DETAILS = 'tcs.savedDetails.v1';
  const pick = (object, ...names) => {
    for (const name of names) if (object?.[name] != null && object[name] !== '') return object[name];
    return null;
  };
  // "//host/…" and the app's "https://shop.myshopify.com//host/…" both become a plain https address.
  const imageUrl = (src) => {
    if (!src) return null;
    const doubled = String(src).match(/^https?:\/\/[^/]+\/\/(.+)$/);
    if (doubled) return `https://${doubled[1]}`;
    return String(src).startsWith('//') ? `https:${src}` : String(src);
  };
  const savedIdOf = (href) => {
    try {
      return new URL(href, window.location.origin).searchParams.get('c.saved');
    } catch (error) {
      return null;
    }
  };
  const normalise = (design) => {
    const id = String(pick(design, 'id', 'designId') ?? '');
    const href = pick(design, 'configurationUrl', 'productUrl') || pick(design, 'productPath') || '/';
    const thumbnail = pick(design, 'thumbnailUrl') || (id && design?.hasThumbnail === true ? `${API}/${encodeURIComponent(id)}/thumbnail` : null);
    return {
      id,
      title: pick(design, 'productTitle', 'title') || 'Your design',
      href: cleanLink(href),
      path: pick(design, 'productPath') || cleanLink(href).split('?')[0],
      savedId: savedIdOf(href),
      savedAt: Date.parse(pick(design, 'updatedAt', 'createdAt') || '') || 0,
      thumbnail,
      image: imageUrl(pick(design, 'productImageUrl') || pick(design, 'imageUrl')),
    };
  };
  const unwrap = (body) => {
    if (Array.isArray(body)) return body;
    for (const name of ['designs', 'items', 'data']) if (Array.isArray(body?.[name])) return body[name];
    return [];
  };

  // A saved configuration, boiled down to what the theme uses. Configurations never change once saved,
  // so they're kept for the visit.
  const plain = (value) => (Array.isArray(value) ? [...value].map(String).sort().join(',') : String(value));
  const humanise = (code) => {
    const words = String(code || '').replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : '';
  };
  const summarise = (config) => {
    const params = {};
    for (const value of config?.values || []) if (value?.parameterId) params[value.parameterId] = plain(value.value);
    const dim = (axis) => {
      const key = Object.keys(params).find((name) => new RegExp(`(^|\\.)(total)?${axis}Mm$`, 'i').test(name));
      return key ? params[key] : null;
    };
    const size = [dim('width'), dim('height'), dim('depth')].filter(Boolean);
    const range = params['cabinet.material.range'];
    const colour = params['cabinet.material.colour'];
    let finish = '';
    if (range === 'paintable_mdf') finish = 'Paint-ready MDF';
    else if (colour) finish = humanise(colour);
    return { size: size.length >= 2 ? `${size.join(' × ')} mm` : '', finish, params };
  };
  const readDetails = () => {
    try {
      return JSON.parse(sessionStorage.getItem(DETAILS) || '{}');
    } catch (error) {
      return {};
    }
  };
  const details = async (item) => {
    if (!item?.savedId) return null;
    const cached = readDetails();
    if (cached[item.savedId]) return cached[item.savedId];
    try {
      const response = await fetch(`${SAVED_CONFIG}/${encodeURIComponent(item.savedId)}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const summary = summarise(await response.json());
      const all = readDetails();
      all[item.savedId] = summary;
      try {
        sessionStorage.setItem(DETAILS, JSON.stringify(all));
      } catch (error) {}
      return summary;
    } catch (error) {
      return null;
    }
  };

  // Is the design in this address one of these? Every c.* option in the address must match the saved
  // value (the address carries a subset of them), on the same product; or it is a ?c.saved link to it.
  const SKIP = new Set(['c.definitionId', 'c.definitionVersion', 'c.saved', 'c.presetKey', 'c.presetVersion']);
  const matches = (href, item, summary) => {
    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (error) {
      return false;
    }
    if (url.pathname !== item.path) return false;
    const savedId = url.searchParams.get('c.saved');
    if (savedId) return savedId === item.savedId;
    if (!summary) return false;
    const options = [...url.searchParams].filter(([name]) => name.startsWith('c.') && !SKIP.has(name));
    if (!options.length) return false;
    return options.every(([name, value]) => {
      const saved = summary.params[name.slice(2)];
      return saved != null && plain(value.split(',')) === plain(saved.split(','));
    });
  };

  const readCache = () => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE) || 'null');
      if (cached && Date.now() - cached.at < 10 * 60e3) return cached.items;
    } catch (error) {}
    return null;
  };
  const writeCache = (items) => {
    try {
      sessionStorage.setItem(CACHE, JSON.stringify({ at: Date.now(), items }));
    } catch (error) {}
  };
  const dropCache = () => {
    try {
      sessionStorage.removeItem(CACHE);
    } catch (error) {}
  };

  let inflight = null;
  const list = ({ fresh = false } = {}) => {
    if (!signedIn) return Promise.resolve([]);
    if (!fresh) {
      const cached = readCache();
      if (cached) return Promise.resolve(cached);
    }
    if (inflight) return inflight;
    inflight = fetch(API, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(async (response) => {
        const body = await response.json().catch(() => null);
        if (!response.ok) throw Object.assign(new Error(body?.error || `Couldn’t load designs (${response.status})`), { status: response.status });
        const items = unwrap(body).map(normalise).sort((a, b) => b.savedAt - a.savedAt);
        writeCache(items);
        return items;
      })
      .finally(() => (inflight = null));
    return inflight;
  };
  const remove = async (id) => {
    const response = await fetch(`${API}/${encodeURIComponent(id)}`, { method: 'DELETE', credentials: 'same-origin', headers: { Accept: 'application/json' } });
    const body = await response.json().catch(() => null);
    if (!response.ok || body?.ok === false) throw new Error(body?.error || 'Couldn’t remove that design');
    const cached = readCache();
    if (cached) writeCache(cached.filter((item) => item.id !== id));
    window.dispatchEvent(new CustomEvent('tcs:saved-change'));
  };

  // Designs saved on this device before accounts, read-only apart from removing them.
  const localList = () => {
    try {
      const value = JSON.parse(localStorage.getItem(LOCAL) || '[]');
      return Array.isArray(value) ? value.sort((a, b) => b.savedAt - a.savedAt) : [];
    } catch (error) {
      return [];
    }
  };
  const localRemove = (id) => {
    try {
      localStorage.setItem(LOCAL, JSON.stringify(localList().filter((item) => item.id !== id)));
    } catch (error) {}
  };

  window.tcsSaved = { list, remove, signedIn, savedPage, designKey };

  /* ---- Counts in the header and menu ---- */

  const updateCounts = (items) => {
    const count = items ? items.length : 0;
    document.querySelectorAll('[data-tcs-saved-count]').forEach((node) => {
      node.hidden = count === 0;
      node.textContent = count;
    });
    document.querySelectorAll('[data-tcs-saved-link]').forEach((link) => {
      link.setAttribute('aria-label', count ? `Saved designs, ${count}` : 'Saved designs');
    });
  };
  const refreshCounts = (fresh = false) => list({ fresh }).then(updateCounts).catch(() => updateCounts(null));
  window.addEventListener('tcs:saved-change', () => refreshCounts());

  /* ---- Sign in to save: a sheet on a phone, a dialog on a wider screen ---- */

  const signInUrl = (returnTo) => `/customer_authentication/login?return_to=${encodeURIComponent(returnTo)}`;
  let signInSheet;
  const openSignIn = (opener) => {
    if (!signInSheet) {
      signInSheet = document.createElement('dialog');
      signInSheet.className = 'tcs-overlay tcs-signin';
      signInSheet.setAttribute('data-presentation', 'dialog');
      signInSheet.setAttribute('aria-labelledby', 'TcsSignInTitle');
      signInSheet.innerHTML = `
        <div class="tcs-overlay__handle" aria-hidden="true"></div>
        <header class="tcs-overlay__header">
          <h2 class="tcs-overlay__title" id="TcsSignInTitle">Save this design</h2>
          <button type="button" class="tcs-icon-button" data-tcs-close aria-label="Close">${svg(ICON.x, 20)}</button>
        </header>
        <div class="tcs-overlay__body tcs-signin__body">
          <span class="tcs-signin__mark">${svg(ICON.bookmark, 24)}</span>
          <p class="tcs-body tcs-signin__lead">Sign in and we’ll keep it in your account, exactly as it is now, ready to pick up on any device.</p>
          <p class="tcs-meta tcs-signin__note">New here? Enter your email and we’ll make your account in the same step. Your design saves as soon as you’re back.</p>
        </div>
        <footer class="tcs-overlay__footer tcs-signin__footer">
          <button type="button" class="tcs-button" data-signin-copy><span class="tcs-button__label">${svg(ICON.link)}Copy link instead</span></button>
          <a class="tcs-button tcs-button--primary" data-signin-go href="#"><span class="tcs-button__label">Sign in to save</span></a>
        </footer>`;
      document.body.append(signInSheet);
      signInSheet.querySelector('[data-signin-go]').addEventListener('click', (event) => {
        event.preventDefault();
        const here = window.location.pathname + window.location.search;
        try {
          sessionStorage.setItem(PENDING, JSON.stringify({ key: designKey(window.location.href), path: window.location.pathname, at: Date.now() }));
        } catch (error) {}
        window.location.assign(signInUrl(here));
      });
      signInSheet.querySelector('[data-signin-copy]').addEventListener('click', async () => {
        await copyLink(window.location.href, 'It opens this exact design, size and finish.');
        signInSheet.querySelector('[data-tcs-close]').click();
      });
    }
    signInSheet.returnFocusTo = opener;
    signInSheet.removeAttribute('data-closing');
    signInSheet.showModal();
  };

  const copyLink = async (href, description) => {
    const link = cleanLink(href, true);
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: 'Link copied', description });
    } catch (error) {
      window.prompt('Copy this link to your design:', link);
    }
  };

  /* ---- On a product page: the app's Save button ---- */

  const SAVE = '[data-cfg-save-design]';
  let saved = [];
  let summaries = {};
  // The account's designs, with the values of those for this product, to recognise the one on screen.
  const loadSaved = (fresh = false) =>
    list({ fresh }).then(async (items) => {
      saved = items;
      const here = items.filter((item) => item.path === window.location.pathname);
      const found = await Promise.all(here.map((item) => details(item)));
      summaries = Object.fromEntries(here.map((item, i) => [item.id, found[i]]));
      updateCounts(items);
      paintAll();
      return items;
    });

  const isSaved = () => saved.find((item) => matches(window.location.href, item, summaries[item.id]));
  const paintButton = (button) => {
    if (!button) return;
    if (!button.querySelector('.tcs-buy-save__icon')) {
      button.insertAdjacentHTML('afterbegin', `<span class="tcs-buy-save__icon">${svg(ICON.bookmark, 20)}</span>`);
      button.classList.add('tcs-buy-save');
    }
    const done = Boolean(isSaved());
    if (button.hasAttribute('data-saved') !== done) button.toggleAttribute('data-saved', done);
    const tip = done ? 'Saved' : 'Save design';
    if (button.getAttribute('data-tcs-tip') !== tip) button.setAttribute('data-tcs-tip', tip);
    // The app sets its own label while it saves ("Saving configuration"); ours otherwise.
    if (!button.disabled) {
      const label = done ? 'Saved. In your saved designs' : 'Save design';
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
    }
    if (button.title) button.title = '';
  };
  const paintAll = () => document.querySelectorAll(SAVE).forEach(paintButton);

  // The app redraws its button as the design changes; keep ours on it.
  const watchButtons = () => {
    let queued = false;
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          // The app's own confirmation: replace it with the toast.
          const message = node.matches?.('[data-configurator-purchase-tone="success"]') ? node : node.querySelector?.('[data-configurator-purchase-tone="success"]');
          if (message && /saved/i.test(message.textContent || '')) {
            message.remove();
            onSaved();
          }
        }
      }
      if (!queued) {
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          paintAll();
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  const onSaved = () => {
    toast({
      title: 'Design saved',
      description: 'It’s in your account, ready on any device.',
      action: { label: 'View', href: savedPage },
    });
    dropCache();
    loadSaved(true).catch(() => {});
  };

  // Before the app's own handler: signed out, ask first; already saved, say so rather than save twice.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(SAVE);
      if (!button || button.disabled) return;
      if (!signedIn) {
        event.preventDefault();
        event.stopImmediatePropagation();
        openSignIn(button);
        return;
      }
      const existing = isSaved();
      if (existing) {
        event.preventDefault();
        event.stopImmediatePropagation();
        toast({ title: 'Already saved', description: 'This exact design is in your saved designs.', action: { label: 'View', href: savedPage } });
      }
    },
    true,
  );

  // Back from signing in with a design waiting: save it once the configurator is ready.
  const resumePendingSave = () => {
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(PENDING) || 'null');
      sessionStorage.removeItem(PENDING);
    } catch (error) {}
    if (!signedIn || !pending || pending.path !== window.location.pathname || Date.now() - pending.at > 30 * 60e3) return;
    let tries = 0;
    const attempt = () => {
      const button = document.querySelector(SAVE);
      if (button && !button.disabled && designKey(window.location.href)) {
        if (!isSaved()) button.click();
        else toast({ title: 'Already saved', description: 'This design was already in your account.', action: { label: 'View', href: savedPage } });
        return;
      }
      if (++tries < 60) setTimeout(attempt, 500);
    };
    // The list first, so a design that's already there isn't saved twice.
    loadSaved(true)
      .catch(() => {})
      .finally(attempt);
  };

  /* ---- Share, beside the product's name ----
   * The address is the whole design, so sharing it hands over this exact cabinet. A phone offers its
   * share sheet; elsewhere the link is copied.
   */
  const setupShare = () => {
    const title = document.querySelector('.wt-product__name');
    const isConfigurator = new URLSearchParams(window.location.search).has('c.definitionId') || document.querySelector(SAVE);
    if (document.querySelector('.tcs-share')) return true;
    if (!title || !isConfigurator) return false;
    const share = document.createElement('button');
    share.type = 'button';
    share.className = 'tcs-button tcs-button--compact tcs-share';
    share.innerHTML = `<span class="tcs-button__label">${svg(ICON.share)}Share</span>`;
    title.insertAdjacentElement('afterend', share);
    share.addEventListener('click', async () => {
      const link = cleanLink(window.location.href, true);
      const data = { title: `${title.textContent.trim()} | The Cabinet Shop`, text: 'A cabinet I’ve designed', url: link };
      if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
        try {
          await navigator.share(data);
        } catch (error) {}
        return;
      }
      copyLink(link, 'It opens this exact design, size and finish.');
    });
    return true;
  };

  const setupProduct = () => {
    if (!document.querySelector('.wt-product__name, [data-configurator-layout]')) return;
    watchButtons();
    if (signedIn) loadSaved().catch(() => {});
    // Remember the design in progress (not just saved ones), once the visitor has changed something,
    // so other pages can offer to pick it up again.
    let touched = false;
    const info = document.querySelector('.wt-product__info');
    info?.addEventListener('pointerdown', () => (touched = true), { capture: true });
    info?.addEventListener('keydown', () => (touched = true), { capture: true });
    const remember = () => {
      const title = document.querySelector('.wt-product__name');
      try {
        localStorage.setItem(LAST, JSON.stringify({
          url: designKey(window.location.href),
          title: (title?.textContent || '').trim(),
          image: document.querySelector('meta[property="og:image"]')?.content || '',
          at: Date.now(),
        }));
      } catch (error) {}
    };
    // The configurator rewrites the address as options change: keep the button honest about this design.
    let last = window.location.search;
    setInterval(() => {
      if (window.location.search !== last) {
        last = window.location.search;
        paintAll();
        if (touched) remember();
      }
    }, 700);
    let tries = 0;
    const attempt = () => {
      if (!setupShare() && ++tries < 20) setTimeout(attempt, 500);
    };
    attempt();
    resumePendingSave();
  };

  /* ---- Continue your design ----
   * Away from the product pages, a returning visitor is offered the design they were last working on:
   * a small card in the corner on a wide screen; on a phone, the homepage's floating "Start designing"
   * becomes "Continue your design". Once per visit; closing it, or following it, puts it away.
   */
  const setupResume = () => {
    if (/\/(products|cart|checkout|account|pages\/my-designs)/.test(window.location.pathname)) return;
    try {
      if (sessionStorage.getItem('tcs.resume.done')) return;
    } catch (error) {}
    let design = null;
    try {
      design = JSON.parse(localStorage.getItem(LAST) || 'null');
    } catch (error) {}
    if (!design?.url || Date.now() - design.at > 30 * 864e5) return;

    const done = () => {
      try {
        sessionStorage.setItem('tcs.resume.done', '1');
      } catch (error) {}
    };
    const floatBar = document.querySelector('.tcs-action-bar--float .tcs-button');
    if (floatBar && window.matchMedia('(max-width: 899px)').matches) {
      floatBar.setAttribute('href', design.url);
      floatBar.removeAttribute('data-tcs-open');
      const label = floatBar.querySelector('.tcs-button__label') || floatBar;
      const icon = label.querySelector('svg')?.outerHTML || '';
      label.innerHTML = `Continue your design${icon}`;
      floatBar.addEventListener('click', done);
      return;
    }
    const card = document.createElement('aside');
    card.className = 'tcs-resume';
    card.setAttribute('aria-label', 'Continue your design');
    card.innerHTML = `
      <a class="tcs-resume__link" href="${escapeHtml(design.url)}">
        ${design.image ? `<span class="tcs-resume__image"><img src="${escapeHtml(thumbFromImage(design.image, 96))}" alt="" width="48" height="48"></span>` : ''}
        <span class="tcs-resume__text">
          <span class="tcs-resume__label">Continue your design</span>
          <span class="tcs-resume__title">${escapeHtml(design.title || 'Your cabinet')}</span>
        </span>
        ${svg(ICON.arrow).replace('class="tcs-icon"', 'class="tcs-icon tcs-resume__arrow"')}
      </a>
      <button type="button" class="tcs-icon-button tcs-resume__close" aria-label="Not now">${svg(ICON.x, 16)}</button>`;
    card.querySelector('.tcs-resume__link').addEventListener('click', done);
    card.querySelector('.tcs-resume__close').addEventListener('click', () => {
      done();
      card.setAttribute('data-leaving', '');
      setTimeout(() => card.remove(), 200);
    });
    setTimeout(() => {
      document.body.append(card);
      requestAnimationFrame(() => card.setAttribute('data-shown', ''));
    }, 1400);
  };

  /* ---- The saved designs page ---- */

  const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const thumbFromImage = (src, width = 600) => {
    try {
      const url = new URL(src, window.location.origin);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(url.href)) url.searchParams.set('width', String(width));
      return url.href;
    } catch (error) {
      return src;
    }
  };

  const setupSavedPage = () => {
    const root = document.querySelector('[data-tcs-saved-page]');
    if (!root) return;
    const grid = root.querySelector('[data-saved-grid]');
    const status = root.querySelector('[data-saved-status]');
    const empty = root.querySelector('[data-saved-empty]');
    const failed = root.querySelector('[data-saved-failed]');
    const count = root.querySelector('[data-saved-count]');
    const confirm = root.querySelector('[data-saved-confirm]');
    const more = root.querySelector('[data-saved-more]');
    // The account page shows only the newest few, with "See all" when there are more.
    const limit = Number(root.dataset.limit) || Infinity;
    let items = [];

    const meta = (item) =>
      [item.size, item.finish, item.savedAt ? `Saved ${dateFormat.format(item.savedAt)}` : ''].filter(Boolean).map((t) => `<span>${escapeHtml(t)}</span>`).join('');
    // Size and finish come from each design's saved configuration, filled in as they arrive.
    const enrich = () =>
      items.forEach((item) =>
        details(item).then((summary) => {
          if (!summary) return;
          item.size = summary.size;
          item.finish = summary.finish;
          const node = grid.querySelector(`[data-id="${CSS.escape(item.id)}"] [data-meta]`);
          if (node) node.innerHTML = meta(item);
        }),
      );
    const card = (item) => {
      const picture = item.thumbnail || (item.image && thumbFromImage(item.image));
      const fallback = item.image ? thumbFromImage(item.image) : '';
      return `
        <li class="tcs-saved-card" data-id="${escapeHtml(item.id)}">
          <a class="tcs-card__link tcs-saved-card__link" href="${escapeHtml(item.href)}">
            <span class="tcs-card__stage tcs-saved-card__stage">
              ${
                picture
                  ? `<img class="tcs-saved-card__image" src="${escapeHtml(picture)}" alt="" loading="lazy" decoding="async"${fallback && fallback !== picture ? ` data-fallback="${escapeHtml(fallback)}"` : ''}>`
                  : `<span class="tcs-card__placeholder">${svg(ICON.bookmark, 28)}</span>`
              }
            </span>
            <span class="tcs-card__body">
              <span class="tcs-card__title">${escapeHtml(item.title)}${svg(ICON.arrow, 16).replace('class="tcs-icon"', 'class="tcs-icon tcs-card__arrow"')}</span>
              <span class="tcs-card__meta" data-meta>${meta(item)}</span>
            </span>
          </a>
          <div class="tcs-saved-card__actions">
            <button type="button" class="tcs-icon-button tcs-icon-button--compact" data-copy aria-label="Copy link to ${escapeHtml(item.title)}" data-tcs-tip="Copy link">${svg(ICON.link)}</button>
            <button type="button" class="tcs-icon-button tcs-icon-button--compact" data-remove aria-label="Remove ${escapeHtml(item.title)}" data-tcs-tip="Remove">${svg(ICON.trash)}</button>
          </div>
        </li>`;
    };

    const render = () => {
      if (count) {
        count.textContent = items.length ? (items.length === 1 ? '1 design' : `${items.length} designs`) : '';
        count.hidden = !items.length;
      }
      grid.innerHTML = items.slice(0, limit).map(card).join('');
      if (more) more.hidden = items.length <= limit;
      grid.hidden = items.length === 0;
      empty.hidden = items.length > 0;
      grid.querySelectorAll('img[data-fallback]').forEach((img) =>
        img.addEventListener('error', () => {
          if (img.dataset.fallback && img.src !== img.dataset.fallback) img.src = img.dataset.fallback;
        }, { once: true }),
      );
      grid.querySelectorAll('img:not([data-fallback])').forEach((img) =>
        img.addEventListener('error', () => img.replaceWith(Object.assign(document.createElement('span'), { className: 'tcs-card__placeholder', innerHTML: svg(ICON.bookmark, 28) })), { once: true }),
      );
    };

    const load = (fresh) => {
      if (!signedIn || !grid) return;
      root.setAttribute('data-state', 'loading');
      failed.hidden = true;
      list({ fresh })
        .then((result) => {
          items = result.map((item) => ({ ...item }));
          root.setAttribute('data-state', 'ready');
          render();
          enrich();
          updateCounts(items);
        })
        .catch(() => {
          root.setAttribute('data-state', 'failed');
          grid.hidden = true;
          empty.hidden = true;
          failed.hidden = false;
        });
    };

    // Remove asks first, naming the design: it can't be undone.
    let pendingRemove = null;
    const askRemove = (item, opener) => {
      pendingRemove = item;
      // Two designs can share a product's name: the size tells them apart.
      confirm.querySelector('[data-confirm-name]').textContent = item.size ? `${item.title}, ${item.size}` : item.title;
      confirm.returnFocusTo = opener;
      confirm.removeAttribute('data-closing');
      confirm.showModal();
    };
    confirm?.querySelector('[data-confirm-remove]').addEventListener('click', async (event) => {
      const item = pendingRemove;
      if (!item) return;
      const button = event.currentTarget;
      window.tcs?.buttonState?.(button, 'working');
      try {
        await remove(item.id);
        items = items.filter((entry) => entry.id !== item.id);
        confirm.querySelector('[data-tcs-close]').click();
        const node = grid.querySelector(`[data-id="${CSS.escape(item.id)}"]`);
        const finish = () => {
          render();
          updateCounts(items);
        };
        if (node && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          node.setAttribute('data-leaving', '');
          setTimeout(finish, 200);
        } else finish();
        toast({ title: 'Removed', description: item.title });
      } catch (error) {
        toast({ title: 'Couldn’t remove that design', description: 'Please try again in a moment.', tone: 'danger' });
      } finally {
        window.tcs?.buttonState?.(button, 'idle');
        pendingRemove = null;
      }
    });

    grid?.addEventListener('click', (event) => {
      const node = event.target.closest('[data-id]');
      if (!node) return;
      const item = items.find((entry) => entry.id === node.dataset.id);
      if (!item) return;
      if (event.target.closest('[data-copy]')) copyLink(item.href, 'Anyone with it can open this design.');
      else if (event.target.closest('[data-remove]')) askRemove(item, event.target.closest('[data-remove]'));
    });
    root.querySelector('[data-saved-retry]')?.addEventListener('click', () => load(true));
    load(true);

    // Designs saved on this device before accounts.
    const device = root.querySelector('[data-saved-device]');
    const deviceList = root.querySelector('[data-saved-device-list]');
    const renderDevice = () => {
      const local = localList();
      if (!device || !deviceList) return;
      device.hidden = local.length === 0;
      deviceList.innerHTML = local
        .map(
          (item) => `
          <li class="tcs-saved__item" data-local-id="${escapeHtml(item.id)}">
            <a class="tcs-row" href="${escapeHtml(item.url)}">
              <span class="tcs-row__leading">${item.image ? `<img src="${escapeHtml(thumbFromImage(item.image, 240))}" alt="" width="56" height="56" loading="lazy">` : ''}</span>
              <span class="tcs-row__body">
                <span class="tcs-row__title">${escapeHtml(item.title)}</span>
                <span class="tcs-row__meta">${[item.size, item.savedAt ? 'Saved ' + dateFormat.format(item.savedAt) : ''].filter(Boolean).map(escapeHtml).join(' · ')}</span>
              </span>
              ${svg(ICON.arrow, 18).replace('class="tcs-icon"', 'class="tcs-icon tcs-row__trailing"')}
            </a>
            <button type="button" class="tcs-icon-button tcs-icon-button--compact tcs-saved__remove" data-local-remove aria-label="Remove ${escapeHtml(item.title)} from this device" data-tcs-tip="Remove">${svg(ICON.trash)}</button>
          </li>`,
        )
        .join('');
    };
    deviceList?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-local-remove]');
      if (!button) return;
      localRemove(button.closest('[data-local-id]').dataset.localId);
      renderDevice();
      toast({ title: 'Removed from this device' });
    });
    renderDevice();
  };

  const init = () => {
    if (signedIn) refreshCounts();
    else {
      dropCache();
      updateCounts(null);
    }
    setupSavedPage();
    setupProduct();
    setupResume();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
